import { getEndMissionDate, getStartMissionDate } from '@/helpers/date';
import {
	CancelNotificationWarning,
	type WorkersSelectionModal,
} from '@/modules/companyNeed/types/missions';
import {
	type MissionPeriod,
	MissionState,
} from '@/modules/companyNeed/views/details/missions/types';
import { WorkerMissionStatus } from '@/types/mission';

interface CancelNotificationMission {
	states: MissionState[];
	periods: MissionPeriod[];
	workersSelection: WorkersSelectionModal[];
}

const getCancelMissionTiming = (
	now: number,
	minStart: number,
	maxEnd: number
): CancelNotificationWarning => {
	if (now > maxEnd) {
		return CancelNotificationWarning.ENDED;
	}

	if (now < minStart) {
		return CancelNotificationWarning.NOT_STARTED;
	}

	return CancelNotificationWarning.IN_PROGRESS;
};

export const getCancelNotificationWarning = (
	mission: CancelNotificationMission,
	now: number = Date.now()
): CancelNotificationWarning | null => {
	const workersSelection = mission.workersSelection ?? [];
	const states = mission.states ?? [];
	const periods = mission.periods ?? [];

	const hasConfirmed = workersSelection.some(
		(selection) =>
			selection.workerStatus === WorkerMissionStatus.CONFIRMED &&
			(selection.workers?.length ?? 0) > 0
	);

	if (!hasConfirmed) {
		return null;
	}

	if (states.includes(MissionState.EXPIRED)) {
		return null;
	}

	if (!periods.length) {
		return null;
	}

	const minStart = getStartMissionDate(periods);
	const maxEnd = getEndMissionDate(periods);

	if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd)) {
		return null;
	}

	const timing = getCancelMissionTiming(now, minStart, maxEnd);

	switch (timing) {
		case CancelNotificationWarning.NOT_STARTED:
			return CancelNotificationWarning.NOT_STARTED;
		case CancelNotificationWarning.IN_PROGRESS:
			return CancelNotificationWarning.IN_PROGRESS;
		case CancelNotificationWarning.ENDED:
			return CancelNotificationWarning.ENDED;
		default: {
			const exhaustiveCheck: never = timing;
			return exhaustiveCheck;
		}
	}
};
